import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../../api';

const statusLabels = { en_attente: 'En attente', prete: 'Prête', retiree: 'Retirée' };
const statusBorderColors = {
  en_attente: 'border-l-[#D97706]',
  prete: 'border-l-[#6B7F3B]',
  retiree: 'border-l-gray-300',
};
const statusBadge = {
  en_attente: 'bg-[#D97706]/10 text-[#D97706] border border-[#D97706]/30',
  prete: 'bg-[#6B7F3B]/10 text-[#6B7F3B] border border-[#6B7F3B]/30',
  retiree: 'bg-gray-100 text-[#8B7355] border border-[#E2D1B8]',
};
const filterActiveColors = {
  '': 'bg-[#C4673D]/10 text-[#C4673D] border-[#C4673D]/40',
  en_attente: 'bg-[#D97706]/10 text-[#D97706] border-[#D97706]/40',
  prete: 'bg-[#6B7F3B]/10 text-[#6B7F3B] border-[#6B7F3B]/40',
  retiree: 'bg-gray-100 text-[#8B7355] border-[#E2D1B8]',
};

export default function BOOrders() {
  const { standId } = useParams();
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('');
  const [notified, setNotified] = useState(null);

  const fetchOrders = useCallback(() => {
    api.getStandOrders(standId, filter || undefined).then(setOrders);
  }, [standId, filter]);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 10000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  const handleNotify = async (orderId) => {
    await api.notifyOrder(orderId);
    setNotified(orderId);
    setTimeout(() => setNotified(null), 3000);
    fetchOrders();
  };

  const handleStatus = async (orderId, statut) => {
    await api.updateOrderStatus(orderId, statut);
    fetchOrders();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-[#332A1C] text-2xl">Commandes en cours</h1>
        <div className="flex gap-2">
          {['', 'en_attente', 'prete', 'retiree'].map(s => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition ${filter === s ? filterActiveColors[s] : 'bg-transparent text-[#8B7355] border-[#E2D1B8] hover:border-[#C4673D] hover:text-[#C4673D]'}`}
            >
              {s ? statusLabels[s] : 'Toutes'}
            </button>
          ))}
        </div>
      </div>

      {notified && (
        <div className="mb-4 bg-[#6B7F3B]/10 border border-[#6B7F3B]/30 text-[#6B7F3B] px-4 py-3 rounded-lg font-medium">
          Notification envoyée pour la commande #{notified}
        </div>
      )}

      {orders.length === 0 ? (
        <div className="text-center py-20 text-[#8B7355]">
          <p className="text-5xl mb-4">📋</p>
          <p>Aucune commande</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {orders.map(order => (
            <div key={order.id} className={`bg-white rounded-xl shadow-sm border-l-4 ${statusBorderColors[order.statut]} p-4`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <span className="text-lg font-bold text-[#332A1C]">#{order.id}</span>
                  <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${statusBadge[order.statut]}`}>
                    {statusLabels[order.statut]}
                  </span>
                </div>
                <span className="text-sm text-[#8B7355]">
                  {order.reference_localisation && `📍 ${order.reference_localisation}`}
                </span>
              </div>

              {order.client && (
                <p className="text-sm text-[#8B7355] mb-2">
                  {order.client.prenom} {order.client.nom} — <span className="text-[#8B7355]/70">{order.client.telephone}</span>
                </p>
              )}

              <div className="border-t border-[#E2D1B8] pt-2 mt-2 space-y-1">
                {order.items.map(item => (
                  <div key={item.id} className="flex justify-between text-sm">
                    <span className="text-[#332A1C]">{item.quantite}x {item.product_nom}</span>
                    <span className="text-[#8B7355]">{(item.prix_unitaire * item.quantite).toFixed(2)} &euro;</span>
                  </div>
                ))}
              </div>

              <div className="mt-3 pt-3 border-t border-[#E2D1B8] flex items-center justify-between">
                <span className="font-bold text-[#332A1C]">{order.total_ttc.toFixed(2)} &euro;</span>
                <div className="flex gap-2">
                  {order.statut === 'en_attente' && (
                    <button
                      onClick={() => handleNotify(order.id)}
                      className="bg-[#B91C1C] text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-[#B91C1C]/90 transition"
                    >
                      📱 Notifier le client
                    </button>
                  )}
                  {order.statut === 'prete' && (
                    <button
                      onClick={() => handleStatus(order.id, 'retiree')}
                      className="bg-gray-100 text-[#332A1C] px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 transition"
                    >
                      Marquer retirée
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
